import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
} from '@mui/material';
import {
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const FooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#0e3158',
  color: 'white',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(3),
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  fontSize: '1.1rem',
  color: '#f0b94d',
}));

const FooterLink = styled(Link)(({ theme, $active }) => ({
  color: $active ? '#f0b94d' : 'rgba(255, 255, 255, 0.9)',
  textDecoration: 'none',
  marginBottom: theme.spacing(0.8),
  display: 'block',
  fontSize: '0.9rem',
  '&:hover': {
    color: '#f0b94d',
    textDecoration: 'none',
  },
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1.5),
  '& svg': {
    marginRight: theme.spacing(1),
    marginTop: '2px',
    fontSize: '1.1rem',
    color: '#f0b94d',
  },
}));

const CopyrightSection = styled(Box)(({ theme }) => ({
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  paddingTop: theme.spacing(2),
  marginTop: theme.spacing(3),
  textAlign: 'center',
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.85rem',
}));

function Footer() {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  
  // Check if a path is active
  const isActive = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };
  
  return (
    <FooterContainer component="footer">
      <Container maxWidth="lg">
        <Grid container spacing={4} justifyContent="center" alignItems="flex-start">

          {/* Quick Links Column */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <FooterTitle variant="h6">Quick Links</FooterTitle>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FooterLink component={RouterLink} to="/about-us" $active={isActive('/about-us')}>
                  About Us
                </FooterLink>
                <FooterLink component={RouterLink} to="/practice-areas" $active={isActive('/practice-areas')}>
                  Practice Areas
                </FooterLink>
                <FooterLink component={RouterLink} to="/legal-resources" $active={isActive('/legal-resources')}>
                  Legal Resources
                </FooterLink>
                <FooterLink component={RouterLink} to="/contact-us" $active={isActive('/contact-us')}>
                  Contact
                </FooterLink>
              </Box>
            </Box>
          </Grid>

          {/* Contact Info Column */}
          <Grid item xs={12} sm={6} md={5}>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <FooterTitle variant="h6">Contact Us</FooterTitle>
              
              <ContactItem>
                <LocationIcon />
                <Typography variant="body2" sx={{ lineHeight: 1.4, fontSize: '0.9rem' }}>
                  Asia Pacific Centre<br />
                  Suite #603 100-4th Avenue SW<br />
                  Calgary AB, T2P 3E8
                </Typography>
              </ContactItem>
              
              <ContactItem>
                <PhoneIcon />
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                    877-529-9498
                  </Typography>
                </Box>
              </ContactItem>
              
              <ContactItem>
                <EmailIcon />
                <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                  info@sentrylaw.ca
                </Typography>
              </ContactItem>
            </Box>
          </Grid>

          {/* Office Hours Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <FooterTitle variant="h6">Office Hours</FooterTitle>
              <ContactItem>
                <ScheduleIcon />
                <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                  Mon - Fri: 9:00 AM - 5:00 PM<br />
                  Sat - Sun: Closed
                </Typography>
              </ContactItem>
            </Box>
          </Grid>
        </Grid>

        <CopyrightSection>
          <Typography variant="body2" sx={{ mb: 1 }}>
            © {currentYear} Sentry Legal Solutions. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Link href="#" color="inherit" sx={{ fontSize: '0.8rem', '&:hover': { color: '#f0b94d' } }}>
              Privacy Policy
            </Link>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>•</Typography>
            <Link href="#" color="inherit" sx={{ fontSize: '0.8rem', '&:hover': { color: '#f0b94d' } }}>
              Terms of Service
            </Link>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>•</Typography>
            <Link href="#" color="inherit" sx={{ fontSize: '0.8rem', '&:hover': { color: '#f0b94d' } }}>
              Disclaimer
            </Link>
          </Box>
        </CopyrightSection>
      </Container>
    </FooterContainer>
  );
}

export default Footer;